import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { AppStore } from '../../state/app.repository';
import { Room, RoomsStore } from '../../state/rooms.repository';
import { UserSettingsStore } from '../../state/user-settings.repository';
import { formatElapsed, formatOverrun, formatRemaining } from '../../shared/helpers/duration';
import { meetingTiming } from '../../shared/models/schedule';
import { UserService } from './user.service';

const SECOND = 1000;
const COARSE_TICK_MS = 15 * SECOND;

/**
 * Live timing of the current meeting: time connected, the scheduled window of
 * the room (when it has a schedule) and how far the meeting runs over it.
 *
 * Every public signal is a primitive so consumers only re-render when the
 * displayed value changes, not on every clock tick. The clock ticks every
 * second only while seconds are on screen (overrun, last minute before a
 * boundary); otherwise every 15 s.
 *
 * Known limitation: hidden rooms (instant meetings) are only in the rooms
 * store after a lookup, so they may show as unscheduled. The join response
 * carries the schedule too, but `OdinService` does not keep it yet.
 */
@Injectable({ providedIn: 'root' })
export class MeetingClockService {
  private readonly _userService = inject(UserService);

  private readonly _connectedView = toSignal(inject(AppStore).isConnectedView$, {
    initialValue: false,
  });
  private readonly _roomId = toSignal(inject(UserSettingsStore).roomId$, { initialValue: '' });
  private readonly _rooms = toSignal(inject(RoomsStore).rooms$, { initialValue: [] as Room[] });
  private readonly _schedule = computed(
    () => this._rooms().find((r) => r.roomId === this._roomId())?.schedule ?? null
  );

  /** When this session started: the own join time. */
  readonly sessionStart = computed(() => this._userService.ownUser()?.data.joinedAt ?? null);

  private readonly _now = signal(Date.now());
  private readonly _timing = computed(() =>
    meetingTiming(this._schedule(), this.sessionStart(), this._now())
  );

  readonly phase = computed(() => this._timing().phase);
  readonly windowStart = computed(() => this._timing().window?.start ?? null);
  readonly windowEnd = computed(() => this._timing().window?.end ?? null);

  /** False when the window is on another day — show its date, not just the time. */
  readonly windowIsToday = computed(() => {
    const start = this.windowStart();
    return (
      start === null || new Date(start).toDateString() === new Date(this._now()).toDateString()
    );
  });

  readonly elapsedLabel = computed(() => {
    const start = this.sessionStart();
    return start === null ? '' : formatElapsed(this._now() - start);
  });

  readonly overrunLabel = computed(() => {
    const timing = this._timing();
    return timing.phase === 'overrun' ? formatOverrun(timing.overrunMs) : '';
  });

  /** Time to the next boundary: until the end while running, until the start while upcoming. */
  readonly remainingLabel = computed(() => {
    const timing = this._timing();
    if (!timing.window) return '';
    if (timing.phase === 'running') return formatRemaining(timing.window.end - this._now());
    if (timing.phase === 'upcoming') return formatRemaining(timing.window.start - this._now());
    return '';
  });

  /** Share of the scheduled window that has elapsed, 0–1 (rounded to 0.1 %). */
  readonly progress = computed(() => {
    const timing = this._timing();
    if (!timing.window) return 0;
    if (timing.phase === 'overrun') return 1;
    if (timing.phase !== 'running') return 0;
    const { start, end } = timing.window;
    const ratio = (this._now() - start) / Math.max(1, end - start);
    return Math.round(Math.min(1, Math.max(0, ratio)) * 1000) / 1000;
  });

  private readonly _active = computed(() => this._connectedView() && this.sessionStart() !== null);
  private readonly _needsSeconds = computed(() => {
    const timing = this._timing();
    if (timing.phase === 'overrun') return true;
    if (!timing.window) return false;
    const now = this._now();
    const nextBoundary = timing.phase === 'upcoming' ? timing.window.start : timing.window.end;
    return Math.abs(nextBoundary - now) <= 60 * SECOND;
  });

  constructor() {
    effect((onCleanup) => {
      if (!this._active()) return;
      const intervalMs = this._needsSeconds() ? SECOND : COARSE_TICK_MS;
      const id = setInterval(() => this._now.set(Date.now()), intervalMs);
      onCleanup(() => clearInterval(id));
    });
    // Catch up immediately when the inputs change (join, schedule update)
    // rather than waiting for the next coarse tick.
    effect(() => {
      this._schedule();
      this.sessionStart();
      this._now.set(Date.now());
    });
  }
}
